import { useState, useEffect, useMemo } from "react";
import { X, Calculator, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

export const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [followers, setFollowers] = useState([1000]);

  useEffect(() => {
    if (dismissed) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 5 && !dismissed) {
        setShow(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [dismissed]);

  const revenue = useMemo(() => {
    const count = followers[0];
    const conversionRate = 0.01;
    const avgPrice = 47;
    const monthly = Math.round(count * conversionRate * avgPrice);
    return { monthly, annual: monthly * 12 };
  }, [followers]);

  const handleDismiss = () => {
    setDismissed(true);
    setShow(false);
  };

  if (!show || dismissed) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Popup */}
      <div className="relative rounded-2xl p-8 max-w-md w-full animate-in fade-in zoom-in-95 duration-300" style={{ background: '#111111', border: '1px solid rgba(245,166,35,0.3)', boxShadow: '0 0 40px rgba(245,166,35,0.1)' }}>
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1 transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(245,166,35,0.15)' }}>
            <Calculator className="w-7 h-7" style={{ color: '#F5A623' }} />
          </div>
          <h3 className="text-2xl font-display font-bold text-white mb-2">
            Wait — See What Your Audience Is Worth
          </h3>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Even a small following can generate real income with the right product.
          </p>
        </div>

        {/* Calculator */}
        <div className="space-y-6 mb-6">
          <div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Your followers</span>
              <span className="text-sm font-bold" style={{ color: '#F5A623' }}>{followers[0].toLocaleString()}</span>
            </div>
            <Slider
              value={followers}
              onValueChange={setFollowers}
              min={100}
              max={100000}
              step={100}
              className="w-full"
            />
            <div className="flex justify-between text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span>100</span>
              <span>100K</span>
            </div>
          </div>

          {/* Results */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.2)' }}>
              <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Monthly</p>
              <p className="text-2xl font-display font-bold" style={{ color: '#F5A623' }}>
                ${revenue.monthly.toLocaleString()}
              </p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.2)' }}>
              <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Annual</p>
              <p className="text-2xl font-display font-bold" style={{ color: '#F5A623' }}>
                ${revenue.annual.toLocaleString()}
              </p>
            </div>
          </div>

          <p className="text-xs text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Based on 1% conversion × $47 product (conservative estimate)
          </p>
        </div>

        <a href="#pricing">
          <Button variant="hero" size="lg" className="w-full group" onClick={handleDismiss}>
            Unlock This Revenue
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </a>
      </div>
    </div>
  );
};
